import {view, css, Action} from '@sail/ui/deprecated';
import {
  MessageDescriptor,
  FormattedMessage,
  useIntl,
  defineMessage,
} from 'react-intl';
import React from 'react';
import {useWidgetControls, useWidgetTitle} from '../useWidget';
import {useTheme} from '../../ThemeProvider';
import {useAnalytics} from '../../AnalyticsProvider';

const defaultTitleMsg = defineMessage({
  id: 'conversations_ui.widget.header.title.label',
  description: 'The aria label applies to the title text in the header',
  defaultMessage: 'Stripe Support',
});

type Props = {
  defaultTitle?:
    | string
    | MessageDescriptor
    | React.ReactNode
    | undefined
    | null;
};

export function HeaderTitle({defaultTitle}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const {toggle} = useWidgetControls();
  const analytics = useAnalytics();

  const {title: widgetTitle} = useWidgetTitle();
  const title = widgetTitle ?? defaultTitle;

  let formattedTitle;
  let label: MessageDescriptor = defaultTitleMsg;
  if (!title) {
    formattedTitle = <FormattedMessage {...defaultTitleMsg} />;
  } else if (typeof title === 'string') {
    formattedTitle = title;
  } else if ('id' in (title as MessageDescriptor)) {
    formattedTitle = <FormattedMessage {...(title as MessageDescriptor)} />;
    label = title as MessageDescriptor;
  } else {
    formattedTitle = title as React.ReactNode;
  }

  // This is in place to prepare for @sail/intl migration which intl.formatMessage can return a ReactNode
  let ariaLabel = intl.formatMessage(label);
  if (typeof ariaLabel !== 'string') {
    ariaLabel = label.defaultMessage || '';
  }

  return (
    <view.h1
      data-testid="conversations-widget-header-title"
      css={{
        width: css.value('100%'),
        font: 'body',
        color: css.value(theme.onBrandTextColor),
        fontWeight: 'bold',
        paddingX: 'xsmall',
        whiteSpace: css.value('nowrap'),
        overflow: css.value('hidden'),
        textOverflow: css.value('ellipsis'),
      }}
      aria-label={ariaLabel}
    >
      <Action
        css={{
          paddingY: 'small',
          display: css.value('block'),
          width: css.value('100%'),
          focusRing: css.value('none'),
        }}
        tabIndex={-1}
        onPress={() => {
          analytics.action('toggle_minimize_header_title_clicked');
          toggle();
        }}
      >
        {formattedTitle}
      </Action>
    </view.h1>
  );
}
